/**
 * Le schede della commessa (GI-237, GI-279).
 *
 * **Panoramica, Banco, Mappa, Foto, Copertura, Anomalie, Consegna** in prima
 * fila: la Panoramica e' la casa della commessa — e' dove porta «Apri la
 * commessa» dal pool — e stava sotto «Altro», dove nessuno cerca la casa.
 * Ingest e Qualita' restano sotto «Altro»: tappe del processo, non posti dove
 * si va tutti i giorni. «Avanzamento» non c'e' piu': era una voce che portava
 * a un'altra voce, con un rimando.
 *
 * **La scheda «Banco» porta con se' la sessione.** Era `…/queue` e basta: ogni
 * passaggio per un'altra scheda toglieva all'operatore la sessione aperta, e
 * il banco tornava in sola osservazione. Ora la scheda rimette nell'indirizzo
 * la sessione ricordata per la commessa.
 *
 * **«Altro» si chiude al passaggio.** Un menu che resta aperto sulla schermata
 * in cui si e' appena arrivati copre la testa della pagina.
 *
 * Filtrate da `Whoami.may`, come prima: una scheda che porta a un rifiuto e'
 * peggio di una scheda che non c'e'.
 */
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import {
  EXPORT_COMPOSE,
  FINDING_MANUAL,
  INGEST_READ,
  QUALITY_SAMPLE,
  REVIEW_QUEUE,
  type Whoami,
} from '../auth'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'
import { recallReviewSession } from '../reviewSessionMemory'
import { ingestPath } from '../routes/InspectionLayout'

interface Tab {
  id: string
  name: MessageKey
  to: string | null
  need: string | null
  end?: boolean
  /** Altri indirizzi che appartengono a questa scheda, oltre a `to`. */
  also?: string[]
}

/** L'indirizzo della scheda «Banco»: con la sessione ricordata, se c'e'. */
export function deskTabPath(inspectionId: string): string {
  const base = `/inspections/${inspectionId}/queue`
  const session = recallReviewSession(inspectionId)
  return session === null ? base : `${base}?session=${encodeURIComponent(session)}`
}

/**
 * Se l'indirizzo e' una delle schede della commessa. Le schermate che non lo
 * sono — la scheda dell'elemento, la ricevuta di caricamento — hanno bisogno
 * che il titolo si veda nel percorso, perche' nessuna scheda e' accesa.
 */
export function isTabScreen(pathname: string, inspectionId: string): boolean {
  const base = `/inspections/${inspectionId}`
  if (pathname === base) return true
  if (pathname.startsWith('/ingest/')) return true
  const tail = pathname.startsWith(`${base}/`) ? pathname.slice(base.length + 1).split('/')[0] : ''
  return ['queue', 'candidates', 'map', 'search', 'coverage', 'anomalies', 'delivery', 'quality'].includes(tail ?? '')
}

export function WorkspaceTabs({
  inspectionId,
  who,
  jobId = null,
}: {
  inspectionId: string
  who: Whoami
  jobId?: string | null
}): JSX.Element {
  const t = useT()
  const location = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)
  const base = `/inspections/${inspectionId}`
  const ingestTo = jobId !== null && jobId !== '' ? ingestPath(jobId, inspectionId) : null
  const first: Tab[] = [
    { id: 'overview', name: 'nav.overview', to: base, need: null, end: true },
    { id: 'queue', name: 'tab.queue', to: deskTabPath(inspectionId), need: REVIEW_QUEUE, also: [`${base}/queue`, `${base}/candidates`] },
    { id: 'map', name: 'workspace.map', to: `${base}/map`, need: REVIEW_QUEUE },
    { id: 'photos', name: 'workspace.photos', to: `${base}/search`, need: FINDING_MANUAL },
    { id: 'coverage', name: 'nav.coverage', to: `${base}/coverage`, need: FINDING_MANUAL },
    { id: 'anomalies', name: 'workspace.anomalies', to: `${base}/anomalies`, need: REVIEW_QUEUE },
    { id: 'delivery', name: 'workspace.delivery', to: `${base}/delivery`, need: EXPORT_COMPOSE },
  ]
  const more: Tab[] = [
    { id: 'ingest', name: 'nav.ingest', to: ingestTo, need: INGEST_READ },
    { id: 'quality', name: 'nav.quality', to: `${base}/quality`, need: QUALITY_SAMPLE },
  ]
  const allowed = (one: Tab): boolean => one.to !== null && (one.need === null || who.may.includes(one.need))
  const on = (one: Tab, isActive: boolean): boolean =>
    isActive || (one.also ?? []).some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`))
  const link = (one: Tab): JSX.Element => (
    <NavLink
      key={one.id}
      to={one.to ?? ''}
      end={one.end === true}
      className={({ isActive }) => (on(one, isActive) ? 'workspace-tab on' : 'workspace-tab')}
      onClick={() => setMoreOpen(false)}
    >
      {t(one.name)}
    </NavLink>
  )
  const rest = more.filter(allowed)
  return (
    <nav className="workspace-tabs" aria-label={t('nav.sections')}>
      {first.filter(allowed).map(link)}
      {rest.length > 0 && (
        <details
          className="workspace-more"
          open={moreOpen}
          onToggle={(event) => setMoreOpen((event.currentTarget as HTMLDetailsElement).open)}
        >
          <summary aria-label={t('workspace.more')}>{t('workspace.more')}</summary>
          <div className="workspace-more-list">{rest.map(link)}</div>
        </details>
      )}
    </nav>
  )
}
